import { useEffect, useState } from 'react';
import {
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  Stack,
  Collapse,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Layout from '../../components/Layout';
import styles from './FrequentUpdates.module.scss';
import { useGetJobs } from '../../api/hooks/useGetJobs';
import type { JobDetailsResponse } from '../../api/contracts/JobsResponse';
import { StartJobDialog } from './StartJobDialog';
import { useCreateJob } from '../../api/hooks/useCreateJob';
import { JobsListSkeleton } from './JobsListSkeleton';
import { RetailersStatusList } from './RetailersStatusList';
import { useGetJobProducts } from '../../api/hooks/useGetJobProducts';
import { useRetryJob } from '../../api/hooks/useRetryJob';
import { formatDate } from '../../utils/formatDate';
import { useResumeJob } from '../../api/hooks/useResumeJob';
import { useStopJob } from '../../api/hooks/useStopJob';
import StopIcon from '@mui/icons-material/Stop';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { ProductsList } from './ProductsList';
import { useSyncToSupabase } from '../../api/hooks/useSyncToSupabase';
import { Notification } from '../../components/Notification';

const calculateDuration = (createdAt: string, updatedAt: string) => {
  const start = new Date(createdAt).getTime();
  const end = new Date(updatedAt).getTime();
  const durationMs = end - start;
  const durationMinutes = Math.max(1, Math.round(durationMs / (1000 * 60)));
  return `${durationMinutes} min`;
};

const getOverallStatus = (job: JobDetailsResponse) => {
  if (!job.retailers || job.retailers.length === 0) return 'PENDING';

  if (job.retailers.some((stat) => stat.status === 'processing'))
    return 'PROCESSING';
  if (job.retailers.some((stat) => stat.status.startsWith('updating_products')))
    return 'UPDATING_PRODUCTS';
  if (job.retailers.some((stat) => stat.status.startsWith('products_updated')))
    return 'PRODUCTS_UPDATED';
  if (job.retailers.every((stat) => stat.status === 'completed'))
    return 'COMPLETED';
  if (job.retailers.every((stat) => stat.status === 'cancelled'))
    return 'CANCELLED';
  if (job.retailers.some((stat) => stat.status === 'failed')) return 'FAILED';

  return 'PENDING';
};

const isJobRetryable = (status: string) => {
  return status === 'FAILED' || status === 'COMPLETED' || 'PRODUCTS_UPDATED';
};

const canSyncToSupabase = (job: JobDetailsResponse) => {
  return job?.retailers?.some((stat) => stat.status === 'completed');
};

export const FrequentUpdates = () => {
  const {
    data,
    isLoading,
    mutate: refreshJobs,
  } = useGetJobs(50, 'frequent_updates', {
    refreshInterval: 10000,
  });

  const { trigger: createJob } = useCreateJob();

  const [jobs, setJobs] = useState<JobDetailsResponse[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [expandedJobs, setExpandedJobs] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [expandedUnavailableJobs, setExpandedUnavailableJobs] = useState<{
    [key: string]: boolean;
  }>({});
  const [selectedJobId, setSelectedJobId] = useState<string>();
  const [stopSelectedJobId, setStopSelectedJobId] = useState<string>();
  const [resumeSelectedJobId, setResumeSelectedJobId] = useState<string>();
  const [selectedUnavailableJobId, setSelectedUnavailableJobId] =
    useState<string>();
  const [retryJobId, setRetryJobId] = useState<string>();
  const [syncToSupabaseJobId, setSyncToSupabaseJobId] = useState<string>();
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationSeverity, setNotificationSeverity] = useState<
    'success' | 'error'
  >('success');

  const { data: jobProducts, isLoading: isJobProductsLoading } =
    useGetJobProducts(selectedJobId ? selectedJobId : undefined, 'failed', 500);

  const { data: unavailableProducts, isLoading: isUnavailableProductsLoading } =
    useGetJobProducts(
      selectedUnavailableJobId ? selectedUnavailableJobId : undefined,
      'unavailable',
      500
    );

  const { trigger: triggerRetryJob } = useRetryJob(retryJobId);
  const { trigger: triggerStopJob } = useStopJob(stopSelectedJobId);
  const { trigger: triggerResumeJob } = useResumeJob(resumeSelectedJobId);
  const { trigger: triggerSyncToSupabase } =
    useSyncToSupabase(syncToSupabaseJobId);

  useEffect(() => {
    if (data) {
      setJobs(data.jobs);
    }
  }, [data]);

  const handleNewJob = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setRetryJobId(undefined);
  };

  const handleRetryJob = (jobId: string) => {
    const job = jobs.find((j) => j.jobId === jobId);
    if (job) {
      setRetryJobId(jobId);
      setOpenDialog(true);
    }
  };

  //   const handleDeleteJob = (jobId: string) => {
  //     // TODO: Implement delete functionality
  //     console.log('Delete job:', jobId);
  //   };

  const toggleFailedProducts = (jobId: string) => {
    const isExpanding = !expandedJobs[jobId];

    setExpandedJobs((prev) => ({
      ...prev,
      [jobId]: isExpanding,
    }));

    if (isExpanding) {
      setSelectedJobId(jobId);
    } else if (selectedJobId === jobId) {
      setSelectedJobId(undefined);
    }
  };

  const toggleUnavailableProducts = (jobId: string) => {
    const isExpanding = !expandedUnavailableJobs[jobId];

    setExpandedUnavailableJobs((prev) => ({
      ...prev,
      [jobId]: isExpanding,
    }));

    if (isExpanding) {
      setSelectedUnavailableJobId(jobId);
    } else if (selectedUnavailableJobId === jobId) {
      setSelectedUnavailableJobId(undefined);
    }
  };

  // Find the job being retried to pass to StartJobDialog
  const retryJob = retryJobId
    ? jobs.find((j) => j.jobId === retryJobId)
    : undefined;

  const handleStartJob = async (
    retailers: string[],
    // @ts-ignore
    status?: string,
    limit?: number
  ) => {
    if (retryJobId) {
      await triggerRetryJob({});
      refreshJobs();
      handleCloseDialog();
      return;
    }
    await createJob({
      body: {
        retailers,
        jobType: 'frequent_updates',
        limit,
      },
    });
    refreshJobs();
    handleCloseDialog();
  };

  useEffect(() => {
    const handleStopJob = async () => {
      if (stopSelectedJobId) {
        await triggerStopJob({});
        refreshJobs();
        setStopSelectedJobId(undefined);
      }
    };
    handleStopJob();
  }, [stopSelectedJobId]);

  useEffect(() => {
    const handleResumeJob = async () => {
      if (resumeSelectedJobId) {
        await triggerResumeJob({});
        refreshJobs();
        setResumeSelectedJobId(undefined);
      }
    };
    handleResumeJob();
  }, [resumeSelectedJobId]);

  useEffect(() => {
    const handleSyncToSupabase = async () => {
      if (syncToSupabaseJobId) {
        try {
          await triggerSyncToSupabase({});
          setNotificationMessage('Successfully started syncing to Supabase');
          setNotificationSeverity('success');
          setNotificationOpen(true);
          refreshJobs();
          setSyncToSupabaseJobId(undefined);
        } catch (error) {
          setNotificationMessage('Failed to sync to Supabase');
          setNotificationSeverity('error');
          setNotificationOpen(true);
          setSyncToSupabaseJobId(undefined);
        }
      }
    };
    handleSyncToSupabase();
  }, [syncToSupabaseJobId]);

  const handleCloseNotification = () => {
    setNotificationOpen(false);
  };

  return (
    <Layout>
      <div className={styles.header}>
        <Typography variant="h4" component="h1">
          Frequent Updates - Jobs
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNewJob}
        >
          New Job
        </Button>
      </div>

      <div className={styles.jobsList}>
        {isLoading ? (
          <JobsListSkeleton />
        ) : (
          <List>
            {jobs.map((job) => (
              <ListItem key={job.jobId} className={styles.jobItem}>
                <ListItemText
                  primary={
                    <div className={styles.jobTitleContainer}>
                      <span className={styles.jobTitle}>{job.jobType}</span>
                      <Stack
                        direction="row"
                        spacing={1}
                        className={styles.actionButtons}
                      >
                        {isJobRetryable(getOverallStatus(job)) &&
                          job.retailers.some((r) => r.productsFailed > 0) && (
                            <Button
                              size="small"
                              startIcon={<RefreshIcon fontSize="small" />}
                              onClick={() => handleRetryJob(job.jobId)}
                            >
                              Retry
                            </Button>
                          )}
                        {getOverallStatus(job) === 'PROCESSING' && (
                          <Button
                            size="small"
                            color="error"
                            startIcon={<StopIcon />}
                            onClick={() => {
                              setStopSelectedJobId(job.jobId);
                            }}
                          >
                            Stop
                          </Button>
                        )}
                        {getOverallStatus(job) === 'CANCELLED' && (
                          <Button
                            size="small"
                            color="primary"
                            startIcon={<PlayArrowIcon />}
                            onClick={() => {
                              setResumeSelectedJobId(job.jobId);
                            }}
                          >
                            Resume
                          </Button>
                        )}
                        {canSyncToSupabase(job) && (
                          <Tooltip title="Only products from retailers that have completed processing will be synced to Supabase.">
                            <Button
                              size="small"
                              startIcon={
                                <img
                                  src="/supabase-logo-icon.svg"
                                  style={{ width: '16px', height: '16px' }}
                                />
                              }
                              onClick={() => setSyncToSupabaseJobId(job.jobId)}
                            >
                              Sync to Supabase
                            </Button>
                          </Tooltip>
                        )}
                      </Stack>
                    </div>
                  }
                  secondary={
                    <>
                      <span className={styles.jobMeta}>
                        Status: {getOverallStatus(job)} • Started:{' '}
                        {formatDate(job.createdAt)} • Duration:{' '}
                        {calculateDuration(
                          job.createdAt,
                          job.updatedAt || job.createdAt
                        )}
                      </span>
                      <RetailersStatusList job={job} />
                      <div className={styles.statusButtons}>
                        {job.retailers.some((r) => r.productsFailed > 0) && (
                          <Button
                            size="small"
                            onClick={() => toggleFailedProducts(job.jobId)}
                            sx={{
                              padding: '0',
                            }}
                            endIcon={
                              <KeyboardArrowDownIcon
                                sx={{
                                  transform: expandedJobs[job.jobId]
                                    ? 'rotate(180deg)'
                                    : 'rotate(0)',
                                  transition: 'transform 0.3s',
                                }}
                              />
                            }
                          >
                            Failed
                          </Button>
                        )}
                        {job.retailers.some(
                          (r) => r.productsUnavailable > 0
                        ) && (
                          <Button
                            size="small"
                            onClick={() => toggleUnavailableProducts(job.jobId)}
                            sx={{
                              padding: '0',
                            }}
                            endIcon={
                              <KeyboardArrowDownIcon
                                sx={{
                                  transform: expandedUnavailableJobs[job.jobId]
                                    ? 'rotate(180deg)'
                                    : 'rotate(0)',
                                  transition: 'transform 0.3s',
                                }}
                              />
                            }
                          >
                            Unavailable
                          </Button>
                        )}
                      </div>

                      {/* Failed Products Section */}
                      {job.retailers.some((r) => r.productsFailed > 0) && (
                        <Collapse in={expandedJobs[job.jobId]}>
                          {job.jobId === selectedJobId && (
                            <ProductsList
                              products={jobProducts?.products || []}
                              isLoading={isJobProductsLoading}
                              type="failed"
                            />
                          )}
                        </Collapse>
                      )}

                      {/* Unavailable Products Section */}
                      {job.retailers.some((r) => r.productsUnavailable > 0) && (
                        <Collapse in={expandedUnavailableJobs[job.jobId]}>
                          {job.jobId === selectedUnavailableJobId && (
                            <ProductsList
                              products={unavailableProducts?.products || []}
                              isLoading={isUnavailableProductsLoading}
                              type="unavailable"
                            />
                          )}
                        </Collapse>
                      )}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </div>

      <StartJobDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onStart={handleStartJob}
        retryJob={retryJob}
      />

      <Notification
        open={notificationOpen}
        message={notificationMessage}
        severity={notificationSeverity}
        onClose={handleCloseNotification}
      />
    </Layout>
  );
};
